import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { FloatingInput } from '@/components/ui/floating-input'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { apiFetch, signInWithToken } from '@/utils'
import { sendEmailVerification } from 'firebase/auth'
import TermsPage from '@/pages/TermsPage'

const signupFormSchema = z
	.object({
		email: z
			.string()
			.email('Invalid email address')
			.max(64, 'Email must be at most 64 characters'),
		username: z
			.string()
			.min(3, 'Username must be 3-20 characters')
			.max(20, 'Username must be 3-20 characters')
			.regex(
				/^(?=.{3,20}$)(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/,
				'Username can only contain letters, numbers, dots, and underscores, and cannot start/end with or have consecutive dots/underscores'
			),
		firstName: z
			.string()
			.min(1, 'First name is required')
			.max(16, 'First name must be at most 16 characters'),
		lastName: z
			.string()
			.min(1, 'Last name is required')
			.max(16, 'Last name must be at most 16 characters'),
		password: z
			.string()
			.min(8, 'Password must be at least 8 characters')
			.regex(
				/^(?=.*[!@#$%^&*-])(?=.*[0-9])(?=.*[A-Z]).{8,}$/,
				'Password must contain at least one uppercase letter, one number, and one special character'
			),
		confirmPassword: z.string().min(8, 'Password confirmation is required'),
		acceptTerms: z.literal(true, {
			errorMap: () => ({ message: 'You must accept the Terms of Service to continue' })
		})
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ['confirmPassword']
	})

type SignupFormData = z.infer<typeof signupFormSchema>

export default function SignupPage() {
	const [isLoading, setIsLoading] = useState(false)
	const [tosOpen, setTosOpen] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const form = useForm<SignupFormData>({
		resolver: zodResolver(signupFormSchema),
		defaultValues: {
			email: '',
			username: '',
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: '',
			acceptTerms: false as unknown as true
		}
	})

	const onSubmit = async (data: SignupFormData) => {
		setIsLoading(true)
		try {
			const signupResponse = await apiFetch('/v1/users', {
				method: 'POST',
				body: JSON.stringify({
					email: data.email,
					username: data.username,
					firstName: data.firstName,
					lastName: data.lastName,
					password: data.password
				})
			})
			const { data: signupResult, error, success } = await signupResponse.json()
			if(!success || !signupResponse.ok) {
				throw new Error(error?.message || 'Failed to create user')
			}
			const userAuthRecord = await signInWithToken(signupResult.token)
			await sendEmailVerification(userAuthRecord)

			toast({
				title: 'Account created successfully',
				description:
					'Please check your email to verify your account before signing in.'
			})
			navigate('/')
		} catch(error: any) {
			toast({
				title: 'Registration failed',
				description: error?.details || error.message || 'Registration failed',
				variant: 'destructive'
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
			<div className="container max-w-lg mx-auto p-4">
				<Card className="p-6 bg-[#3C3C3C] border-0">
					<div className="text-center mb-6">
						<h1 className="text-2xl font-bold text-[#ffffff]">Create an Account</h1>
					</div>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												type="email"
												autoComplete="email"
												label="Email"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput {...field} label="Username"/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												autoComplete="given-name"
												label="First Name"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput {...field} autoComplete="family-name" label="Last Name"/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												type="password"
												{...field}
												autoComplete="new-password"
												label="Password"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="confirmPassword"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												type="password"
												{...field}
												autoComplete="new-password"
												label="Confirm Password"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="acceptTerms"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
												className="border-[#4C4C4C] data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444]"
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm font-normal cursor-pointer text-[#BABABB]">
												I agree to the{' '}
												<Dialog open={tosOpen} onOpenChange={setTosOpen}>
													<DialogTrigger asChild>
														<Button
															type="button"
															variant="link"
															className="p-0 h-auto font-semibold text-[#EF4444] hover:text-[#DC2626]">
															Terms of Service
														</Button>
													</DialogTrigger>
													<DialogContent className="max-h-[80vh] flex flex-col bg-[#18181B] border-[#4C4C4C]">
														<div className="overflow-y-auto flex-1">
															<TermsPage/>
														</div>
														<DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
															<Button
																type="button"
																variant="outline"
																className="border-[#4C4C4C] bg-[#2F2F32] text-[#ffffff] hover:bg-[#3C3C3C]"
																onClick={() => setTosOpen(false)}>
																Close
															</Button>
															<Button
																type="button"
																className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
																onClick={() => {
																	field.onChange(true)
																	setTosOpen(false)
																}}>
																I Agree
															</Button>
														</DialogFooter>
													</DialogContent>
												</Dialog>
											</FormLabel>
											<FormMessage/>
										</div>
									</FormItem>
								)}
							/>

							<Button type="submit" className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white" disabled={isLoading}>
								{isLoading ? 'Creating Account...' : 'Create Account'}
							</Button>
						</form>
					</Form>

					<div className="mt-4 text-center space-y-2">
						<p className="text-sm text-[#BABABB]">
							Already have an account?{' '}
							<Button
								variant="link"
								className="p-0 h-auto font-semibold text-[#EF4444] hover:text-[#DC2626]"
								onClick={() => navigate('/login')}>
								Log in
							</Button>
						</p>
					</div>
				</Card>
			</div>
		</div>
	)
}